/** Dinheiro é sempre trafegado e armazenado em centavos (inteiros). */

const formatters = new Map<string, Intl.NumberFormat>();

function getFormatter(currency: string, locale: string): Intl.NumberFormat {
  const key = `${locale}|${currency}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 });
    formatters.set(key, f);
  }
  return f;
}

/** 12345 -> "R$ 123,45" */
export function formatMoney(cents: number | null | undefined, currency = 'BRL', locale = 'pt-BR'): string {
  const value = (cents ?? 0) / 100;
  // Intl usa espaço não separável; normalizamos para espaço comum (melhor em PDFs/recibos térmicos).
  return getFormatter(currency, locale).format(value).replace(/ /g, ' ');
}

/** 12345 -> "123,45" (sem símbolo) */
export function formatAmount(cents: number | null | undefined, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    (cents ?? 0) / 100,
  );
}

/** Converte reais (number) em centavos, com arredondamento correto. */
export function toCents(reais: number): number {
  return Math.round(reais * 100);
}

/** "1.234,56" | "1234.56" | "R$ 12,00" -> centavos. Retorna null se inválido. */
export function parseMoney(input: string): number | null {
  if (!input) return null;
  let s = input.replace(/[^\d,.-]/g, '');
  if (!s) return null;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) {
    // O último separador é o decimal.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (hasComma) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** Máscara de digitação estilo "caixa eletrônico": dígitos -> centavos. "1234" -> 1234 (R$ 12,34). */
export function digitsToCents(input: string): number {
  const digits = input.replace(/\D/g, '').slice(0, 13);
  return digits ? parseInt(digits, 10) : 0;
}

/** Percentual de desconto entre preço de tabela e preço final (inteiro, arredondado). */
export function discountPercent(listCents: number, finalCents: number): number {
  if (!listCents || finalCents >= listCents) return 0;
  return Math.round(((listCents - finalCents) / listCents) * 100);
}

/** Aplica percentual (0-100) sobre valor em centavos, arredondando ao centavo. */
export function percentOf(cents: number, percent: number): number {
  return Math.round((cents * percent) / 100);
}

/** Quantidades suportam até 3 casas decimais (produtos fracionados: kg, m, l). */
export function roundQty(q: number): number {
  return Math.round(q * 1000) / 1000;
}

export function formatQty(q: number, unit = 'un', locale = 'pt-BR'): string {
  const isInt = Number.isInteger(q);
  const s = new Intl.NumberFormat(locale, { maximumFractionDigits: isInt ? 0 : 3 }).format(q);
  return unit === 'un' ? s : `${s} ${unit}`;
}
